
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, subMonths, format } from "date-fns";
import { es } from "date-fns/locale";

interface House {
  id: number | string;
}

interface MonthlyExpense {
  month: string;
  water: number;
  electricity: number;
  other: number;
}

interface ExpenseQueryResult {
  amount: number;
  expense_date: string;
  expense_categories: {
    name: string;
  }
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP'
  }).format(amount);
};

export const useExpensesData = (houses: House[]) => {
  const currentDate = new Date();
  const lastMonth = subMonths(currentDate, 1);
  const startMonth = startOfMonth(lastMonth);
  const endMonth = endOfMonth(lastMonth);

  // Fetch water and electricity expenses
  const { data: monthlyUtilities = [] } = useQuery<ExpenseQueryResult[]>({
    queryKey: ['monthly-utilities', houses],
    queryFn: async () => {
      if (!houses.length) return [];

      const { data, error } = await supabase
        .from('expenses')
        .select(`
          amount,
          expense_date,
          expense_categories (
            name
          )
        `)
        .in('entity_id', houses.map(house => house.id.toString()))
        .eq('entity_type', 'house')
        .gte('expense_date', startMonth.toISOString())
        .lte('expense_date', endMonth.toISOString());

      if (error) throw error;
      return data || [];
    },
    enabled: houses.length > 0,
  });

  // Calculate total water and electricity expenses for last month
  const totalWaterExpense = monthlyUtilities
    .filter(expense => expense.expense_categories.name.toLowerCase().includes('agua'))
    .reduce((sum, expense) => sum + expense.amount, 0);

  const totalElectricityExpense = monthlyUtilities
    .filter(expense => expense.expense_categories.name.toLowerCase().includes('electricidad'))
    .reduce((sum, expense) => sum + expense.amount, 0);

  return {
    totalWaterExpense: formatCurrency(totalWaterExpense),
    totalElectricityExpense: formatCurrency(totalElectricityExpense),
  };
};

export const useMonthlyExpenses = (houses: House[]) => {
  const currentDate = new Date();
  const sixMonthsAgo = startOfMonth(subMonths(currentDate, 6));

  return useQuery<MonthlyExpense[]>({
    queryKey: ['monthly-expenses', houses],
    queryFn: async () => {
      if (!houses.length) return [];

      const { data, error } = await supabase
        .from('expenses')
        .select(`
          amount,
          expense_date,
          expense_categories (
            name
          )
        `)
        .in('entity_id', houses.map(house => house.id.toString()))
        .eq('entity_type', 'house')
        .gte('expense_date', sixMonthsAgo.toISOString())
        .order('expense_date', { ascending: true });

      if (error) throw error;

      // Group expenses by month and category
      const groupedExpenses: Record<string, MonthlyExpense> = {};
      
      (data || []).forEach(expense => {
        const month = format(new Date(expense.expense_date), 'MMM', { locale: es });
        if (!groupedExpenses[month]) {
          groupedExpenses[month] = {
            month,
            water: 0,
            electricity: 0,
            other: 0,
          };
        }

        const category = expense.expense_categories.name.toLowerCase();
        if (category.includes('agua')) {
          groupedExpenses[month].water += expense.amount;
        } else if (category.includes('electricidad')) {
          groupedExpenses[month].electricity += expense.amount;
        } else {
          groupedExpenses[month].other += expense.amount;
        }
      });

      // Convert to array and ensure we only have the last 6 months
      return Object.values(groupedExpenses).slice(-6);
    },
    enabled: houses.length > 0,
  });
};
